import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const renderIcon = (icon, className) => {
    if (!icon) return null;
    if (typeof icon === 'object' && (icon.iconName || icon.prefix)) {
        return <FontAwesomeIcon icon={icon} className={className} />;
    }
    const C = icon;
    return <C className={className} strokeWidth={2.2} />;
};

/**
 * Filtro de categorías/preparaciones en forma de pills con iconos.
 * value string  -> single-select (click activo = deselecciona)
 * value array   -> multi-select  (click toggle in/out)
 * Cada entry: { value, label, icon? (FA/Lucide), emoji? }
 *
 * collapsible: si hay más categorías de las que caben en `maxRows` filas, se recortan
 * y aparece un botón "Mostrar más / Mostrar menos".
 */
const CategoryRail = ({
    label,
    categories,
    value,
    onChange,
    className,
    size = 'md',
    collapsible = false,
    maxRows = 2,
}) => {
    // CON EL NOMBRE ESCRITO, NO SOLO EL ICONO (Jesús, 9-09, respuesta 7).
    //
    // ES LA QUINTA VUELTA DE ESTA DECISIÓN, y él mismo pidió dejarlo escrito para que no
    // haya una sexta. La serie entera:
    //
    //     28-07  se enseña el nombre siempre   -> revertido el mismo día
    //     28-07  globo fijo en la marcada
    //     09-08  pastillas con el nombre dentro -> FRANCISCO lo mandó deshacer el 11-08
    //     02-09  Jesús lo vuelve a pedir con texto
    //     09-09  Jesús lo confirma y cierra
    //     10-09  y lo REVIERTE en su artefacto:  <- MANDA ESTO
    //
    // Sexta vuelta, y hay que decir cuál pierde: pierde la del 09-09, la de enseñar el nombre
    // siempre. Su artefacto del 10-09 dice «los iconos van solos, sin nombre, y el nombre sale
    // al tocarlos; el de la categoría puesta, con su globito», y la regla de la casa es que ante
    // una contradicción manda lo más reciente. Preguntado a Francisco el 10-09 antes de tocar
    // -- porque el argumento del 09-09 seguía siendo bueno -- y confirmó: «si, 10».
    //
    // Lo que decía la que pierde, para no perderlo:
    //
    // Su motivo, que es el que zanja la discusión: «"Casquería", "lácteos" o "frutos secos"
    // no tienen un icono que se adivine. Y si hay que pinchar para saber qué es, el icono no
    // ahorra nada: obliga a un toque más».
    //
    // La pastilla crece con su texto; el ALTO no cambia, que es de lo que cuelga el cálculo
    // de cuántas filas caben (`fallbackBtnPx`).
    // `min-h` y no `h`: hay nombres mas largos que la pantalla de un movil («Cremas de
    // frutos secos sin grasas y/o azucares añadidos»), y con alto fijo y `nowrap` se salian
    // por la derecha. Asi la pastilla crece hacia abajo y no se sale nunca.
    const btn = size === 'sm' ? 'min-h-8 py-1 px-2.5 gap-1.5' : 'min-h-9 py-1.5 px-3 gap-1.5';

    const isArray = Array.isArray(value);
    const isSelected = (catValue) =>
        isArray ? value.includes(catValue) : value === catValue;

    // Cuando se pueden marcar varias categorias, tener el nombre abierto en todas las marcadas
    // llenaba la pantalla de cajitas superpuestas. Solo se queda abierta la ultima que has tocado.
    const [ultima, setUltima] = useState(null);

    const handleClick = (catValue) => {
        if (isArray) {
            if (catValue === '' || catValue == null) { onChange?.([]); setUltima(null); return; }
            const quitando = value.includes(catValue);
            const next = quitando
                ? value.filter((v) => v !== catValue)
                : [...value, catValue];
            setUltima(quitando ? null : catValue);
            onChange?.(next);
        } else {
            onChange?.(value === catValue ? '' : catValue);
        }
    };

    // Cual lleva el nombre visible. En seleccion simple, la unica marcada (como hasta ahora).
    // En multiple, la ultima tocada; si se quito, la ultima que quede marcada, para que al
    // volver a la pantalla con filtros ya puestos siga viendose uno.
    const destacada = !isArray
        ? (value || null)
        : (ultima && value.includes(ultima)
            ? ultima
            : (value.length ? value[value.length - 1] : null));

    // Recorte a `maxRows` filas: altura = filas*altoPill + (filas-1)*gap.
    // La pill se mide en el DOM (w-8/w-9 son rem y el html usa font-size 17/18px,
    // así que hardcodear 32/36px cortaba el anillo de la seleccionada por abajo).
    const wrapRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const fallbackBtnPx = size === 'sm' ? 36 : 41;
    // +8: deja aire para el anillo (ring) de la pill seleccionada, que se dibuja FUERA del
    // círculo; sin este margen el overflow:hidden del recorte lo cortaba por arriba/abajo.
    const [collapsedMaxH, setCollapsedMaxH] = useState(maxRows * fallbackBtnPx + (maxRows - 1) * 7 + 8);

    useEffect(() => {
        if (!collapsible) return;
        const el = wrapRef.current;
        if (!el) return;
        const check = () => {
            const pill = el.querySelector('button');
            const btnPx = pill ? pill.offsetHeight : fallbackBtnPx;
            const gap = parseFloat(getComputedStyle(el).rowGap) || 7;
            const maxH = maxRows * btnPx + (maxRows - 1) * gap + 8;
            setCollapsedMaxH(maxH);
            setOverflowing(el.scrollHeight > maxH + 2);
        };
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [collapsible, fallbackBtnPx, maxRows, categories]);

    const clampStyle = (collapsible && !expanded) ? { maxHeight: collapsedMaxH, overflow: 'hidden' } : undefined;

    // El nombre de la categoría marcada se queda visible: en tablet y móvil no hay ratón, así
    // que sin esto no se puede saber qué categoría está aplicada. El resto se abren y cierran
    // con el ratón como siempre (`abierta` guarda cuál lo está).
    const [abierta, setAbierta] = useState(null);

    // EL NOMBRE DE LA MARCADA, DENTRO DEL CUADRO (Jesús, 11-09, punto 4).
    //
    // Era un globito de los de flotar, y siempre abierto: al estar la fila pegada al borde
    // de arriba del cuadro, salía FUERA, cortado por la izquierda y montado encima de los
    // iconos. Su arreglo, con sus palabras: «dentro del cuadro y encima de su fila, como una
    // etiqueta normal».
    //
    // El globito se queda para el resto, que es lo que él pidió el 10-09 -- «el nombre sale
    // al tocarlos» --, pero ya no vive abierto encima de nada.
    const nombreDeLaMarcada = (categories.find((c) => c.value === destacada) || {}).label;

    const pills = (
        // Y AIRE TAMBIEN A LOS LADOS (Francisco, 13-09): «Alimentos frecuentes sale cortado
        // por la izquierda cuando esta seleccionado». El anillo de la marcada se pinta FUERA
        // del circulo, y la primera pastilla arranca pegada al borde de esta caja, que va con
        // `overflow:hidden` por el recorte de filas: los 2 px del anillo caian fuera y se
        // cortaban. Es siempre la primera, y la primera es la de los frecuentes.
        // `px-1` da el hueco y `-mx-1` lo devuelve, para que la fila no se mueva de sitio.
        <div ref={wrapRef} style={clampStyle} className="flex items-center gap-1.5 flex-wrap py-1 px-1 -mx-1">
            {categories.map((cat) => {
                const selected = isSelected(cat.value);
                const iconNode = renderIcon(cat.icon, 'w-4 h-4');
                return (
                    <Tooltip
                        key={cat.value || '__all__'}
                        open={abierta === cat.value}
                        onOpenChange={(o) => setAbierta(o ? cat.value : null)}
                    >
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={() => handleClick(cat.value)}
                                aria-label={cat.label}
                                aria-pressed={selected}
                                className={cn(
                                    'flex items-center justify-center rounded-full transition-all max-w-full',
                                    btn,
                                    selected
                                        ? 'bg-brand-orange/10 text-brand-orange ring-2 ring-brand-orange shadow-sm'
                                        : 'bg-card text-muted-foreground border border-border hover:border-brand-orange/40 hover:text-brand-orange'
                                )}
                            >
                                {iconNode || (
                                    <span className="text-base leading-none" aria-hidden>
                                        {cat.emoji || '·'}
                                    </span>
                                )}
                                {/* SIN EL NOMBRE DENTRO (Jesús, 10-09): «los iconos van solos,
                                    sin nombre, y el nombre sale al tocarlos. El de la categoría
                                    puesta, con su globito». El nombre no se pierde: viaja en
                                    `aria-label` y sale en el globo de abajo, que además se queda
                                    FIJO en la categoría marcada (`destacada`). */}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent
                            side="top"
                            sideOffset={6}
                            className="bg-gray-900 text-white text-xs font-semibold px-3 py-1.5 max-w-[260px] text-center"
                        >
                            {cat.label}
                        </TooltipContent>
                    </Tooltip>
                );
            })}
        </div>
    );

    return (
        <TooltipProvider delayDuration={120} skipDelayDuration={120}>
            {collapsible ? (
                /* EL RÓTULO ENCIMA DE SU FILA, NO A LA IZQUIERDA (Jesús, 11-09). «Alimentos
                   frecuentes» ya salía encima de sus iconos y «Preparación» se quedaba a la
                   izquierda, a media altura de la primera fila: dos carriles iguales
                   pintados de dos formas. Los dos van igual, el nombre arriba. */
                <div className={cn('flex flex-col gap-1', className)}>
                    <div className="flex-1 min-w-0">
                        {(label || nombreDeLaMarcada) && (
                            <div className="flex items-center gap-2 mb-1">
                                {label && (
                                    <span className="text-xs font-bold text-muted-foreground flex-shrink-0">
                                        {label}
                                    </span>
                                )}
                                {nombreDeLaMarcada && (
                                    <span className="text-xs font-semibold text-brand-orange truncate"
                                          data-testid="categoria-marcada">
                                        {nombreDeLaMarcada}
                                    </span>
                                )}
                            </div>
                        )}
                        {pills}
                        {overflowing && (
                            <button
                                type="button"
                                onClick={() => setExpanded((e) => !e)}
                                className="mt-1.5 text-xs font-semibold text-brand-orange hover:underline"
                            >
                                {expanded ? 'Mostrar menos' : 'Mostrar más'}
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className={cn('flex flex-col gap-1', className)}>
                    <div className="flex items-center gap-2">
                        {label && (
                            <span className="text-xs font-bold text-muted-foreground mr-1 flex-shrink-0">
                                {label}
                            </span>
                        )}
                        {nombreDeLaMarcada && (
                            <span className="text-xs font-semibold text-brand-orange truncate"
                                  data-testid="categoria-marcada">
                                {nombreDeLaMarcada}
                            </span>
                        )}
                    </div>
                    {pills}
                </div>
            )}
        </TooltipProvider>
    );
};

export default CategoryRail;
